import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { ChartModule } from 'primeng/chart';
import { ChartService } from '../chart.service';
import { BarChart} from '../model/bar-chart';
import { Subscription, tap } from 'rxjs';
import { MessageService } from 'primeng/api';
import { DateTime } from 'luxon';
import { AppService } from 'app/app.service';
import { ErrorLogService } from '@shared/services/errorhandler/error-log.service';
import { ActivatedRoute } from '@angular/router';
import { BreadcrumbService } from '@shared/components/breadcrumb/breadcrumb.service';

@Component({
    selector: 'app-bar-chart',
    imports: [
        CommonModule,
        BreadcrumbComponent,
        ChartModule
    ],
    templateUrl: './bar-chart.component.html',
    styleUrl: './bar-chart.component.scss'
})
export class BarChartComponent implements OnInit, OnDestroy {

  chartData: any;
  options?: any;
  timeByYearData?: BarChart;
  lightModeOptions?: any;
  darkModeOptions?: any;
  previousTheme?: string;
  yearsTotal: number = 0;
  yearHighest: number = 0;
  yearAverage: number = 0;

  theme$?: string;
  sub?: Subscription;

  constructor(
    public appService: AppService,
    private messageSerive: MessageService,
    private chartService: ChartService,
    private errorLogService: ErrorLogService,
    private route: ActivatedRoute,
    private breadcrumbService: BreadcrumbService
  ) { }

  chartResponseData$ = this.chartService.getByYearBarChart().pipe(
    tap( (chartResponse) => {
      if (!chartResponse || !chartResponse.data) {
        this.errorLogService.logError(`[ERROR] - ${DateTime.now().toString()}: No response from chart service`);
        this.messageSerive.add({severity:'error', summary: 'Error', detail: 'No chart data found'});
        this.loadEmptyChart();
        return;
      }
      this.timeByYearData = chartResponse;
      this.formatTimeByYearBarChartData()
      this.calculateTotals(chartResponse);
    })
  );

  
  ngOnInit(): void {
    const url = location.pathname;
    const breadcrumbLabel = this.route.snapshot.data['breadcrumb'] || 'Time By Year';
    this.breadcrumbService.addToBreadcrumbs(url, breadcrumbLabel);

    this.sub = this.appService.theme$.subscribe((theme) => {
      this.theme$ = theme;
    });

    this.lightModeOptions = {
      indexAxis: 'y',
      maintainAspectRatio: false,
      responsive: false,
      aspectRatio: 0.8,
      plugins: {
        legend: {
          labels: {
            color: '#35454c'
          }
        }
      },
      scales: {
        x: {
          ticks: {
            color: '#6b7280',
          },
          grid: {
            color: '#eeecec'
          },
          max: 1000
        },
        y: {
          ticks: {
            color: '#6b7280'
          },
          grid: {
            color: ''
          }
        }
      }
    };

    this.darkModeOptions = {
      indexAxis: 'y',
      maintainAspectRatio: false,
      responsive: false,
      aspectRatio: 0.8,
      plugins: {
        legend: {
          labels: {
            color: '#e7e7e7'
          }
        }
      },
      scales: {
        x: {
          grid: {
            color: '#2b2b2b'
          },
          ticks: {
            color: '#e7e7e7',
            font: {
              weight: 500
            }
          },
          border: {
            color: '#2b2b2b'
          },
          max: 1000
        },
        y: {
          grid: {
            color: ''
          },
          ticks: {
            color: '#e7e7e7'
          },
          border: {
            color: '#2b2b2b'
          }
        }
      }
    };
    this.options = this.lightModeOptions;

  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  convertMinutesToHours(minutes: any): number {
    return +(minutes / 60).toFixed(1);
  }

  calculateTotals(chartData: BarChart) {
    this.yearsTotal = chartData.data.reduce((a, b) => a + b, 0);
    this.yearHighest = Math.max(...chartData.data);
    this.yearAverage = this.yearsTotal / chartData.data.length;
  }

  ngDoCheck(): void {
    const currentTheme = this.theme$;
    if (this.previousTheme !== currentTheme) {
      this.previousTheme = currentTheme;
      this.options = this.toggleThemeOptions();
      if (this.timeByYearData) {
        this.formatTimeByYearBarChartData();
      }
      else {
        this.loadEmptyChart();
      }
    }
  }

  toggleThemeOptions(): any {
    if (this.theme$ === 'light') {
      return this.lightModeOptions;
    } else {
      return this.darkModeOptions;
    }
  }

  formatTimeByYearBarChartData(): void {
    this.chartData = {
        labels: this.timeByYearData?.labels,
        datasets: [
          {
            label: 'Time by Year',
            data: this.timeByYearData?.data,
            backgroundColor: '#1c80cf50',
            borderColor: '#1c80cf',
            hoverBackgroundColor: '#1c80cf',
            borderWidth: 1
          }
        ]
      };
  }

  loadEmptyChart(): void {
    const year = DateTime.now().year;
    this.chartData = {
      labels: [year],
      datasets: [
        {
          label: 'Time by Year',
          data: [0,500,1000],
          backgroundColor: '#1c80cf50',
          borderColor: '#1c80cf',
          borderWidth: 1,
        }
      ]
    };
  }
}
